import api from "@/interceptors";
import { Org, Vendor } from "../apiRoutes/routes";
import { IApiPromise } from "@/interfaces/responseData";
import {
  IChecklistRequested,
  IDataEmailGrant,
  IVendorProvider,
} from "@/interfaces/vendor";
import get from "lodash/get";
import { successStatusCodes } from "@/utils/constants";
import { showErrorAPI } from "../apiRoutes";

type IParamsCreateTemplateItem = {
  name?: string;
  order?: number;
  importanceId?: string;
  complianceTypeId?: string;
  description?: string | null;
  notes?: string | null;
  guidance?: string | null;
  isHeading?: boolean;
  headingLabel?: string | null;
  parentGroupingId1?: number | null;
  parentGroupingId2?: number | null;
  parentGroupingId3?: number | null;
  parentGroupingId4?: number | null;
  itemMasterId?: number | null;
};
export const getRequestedEmailGrant = (params: {
  checklistRequestedId: number;
  emailGrantId: number;
}): IApiPromise<IChecklistRequested[]> =>
  api.get(
    Vendor.getChecklistRequestedEmailGrant
      .replace(":checklistRequestedId", params.checklistRequestedId.toString())
      .replace(":emailGrantId", params.emailGrantId.toString()),
  );

export type IParamsUpdateTemplateItem = {
  checklistRequestedItemId: number;
  complianceTypeValueId?: number;
  compensatingControlValue?: number;
  compensatingControlDescription?: string;
  notes?: string;
};

export interface IChecklistRequestedItem {
  checklistRequestedItemId: number;
  complianceTypeValueId?: number | null;
  compensatingControlDescription?: string;
  notes?: string | null;
}

export type IParamsUpdateChecklistRequestedItems = {
  checklistRequestedItems: IChecklistRequestedItem[];
};

export const getRequestedAssessment = (params?: any): IApiPromise<any> => {
  if (params) return api.get(Vendor.getRequestedAssessment, { params });
  else return api.get(Vendor.getRequestedAssessment);
};

export const updateRequestedAssessment = (
  checklistRequestedId: any,
  params: IParamsUpdateTemplateItem,
): IApiPromise<any> =>
  api.put(
    Vendor.putChecklistRequestedItems.replace(
      ":checklistRequestedId",
      checklistRequestedId,
    ),
    { checklistRequestedItems: params },
  );

export const postChecklistRequested = (requestedAssessment): IApiPromise<any> =>
  api.post(Vendor.postRequestedAssessment, requestedAssessment);

export const createRequestTemplateItem = (
  templateId: any,
  params: IParamsCreateTemplateItem,
): IApiPromise<any> =>
  api.post(
    Vendor.postRequestTemplateItem.replace(":templateId", templateId),
    params,
  );

export const putRequestTemplateItem = (
  templateId: any,
  params: IParamsCreateTemplateItem,
): IApiPromise<any> =>
  api.put(
    Vendor.putRequestTemplateItem.replace(":templateId", templateId),
    params,
  );

export const putChecklistRequested = ({
  checklistRequestedId,
  params,
}): IApiPromise<any> =>
  api.put(
    Vendor.putRequestedAssessment.replace(
      ":checklistRequestedId",
      checklistRequestedId,
    ),
    params,
  );

export const postChecklistRequestedGetSecret = ({
  checklistRequestedId,
  recipientEmail,
  validUntil,
  recipientFirstName,
  recipientLastName,
}): IApiPromise<any> =>
  api.post(
    Vendor.postChecklistRequestedGetSecret.replace(
      ":checklistRequestedId",
      checklistRequestedId,
    ),
    { recipientEmail, validUntil, recipientFirstName, recipientLastName },
  );

export const getChecklistRequestedTemplate = (params: any): IApiPromise<any> =>
  api.get(Vendor.getRequestedTemplate, { params });

export const getRequestedTemplateDetail = (templateId: any): IApiPromise<any> =>
  api.get(Vendor.requestedTemplateDetail.replace(":templateId", templateId));

export const getRequestedDetailItem = (requestedId: any): IApiPromise<any> =>
  api.get(
    Vendor.getRequestedDetailItem.replace(":checklistRequestedId", requestedId),
  );

export const putRequestedUpdatedItems = (
  requestedId: any,
  params: any,
): IApiPromise<any> =>
  api.put(
    Vendor.putRequestedDetailItem.replace(":checklistRequestedId", requestedId),
    { checklistRequestedItems: params },
  );

export const createRequestedTemplate = (data: any): IApiPromise<any> =>
  api.post(Vendor.postChecklistRequestedTemplate, data);
export const updateRequestedTemplate = ({
  templateId,
  params,
}): IApiPromise<any> =>
  api.put(
    Vendor.putChecklistRequestedTemplate.replace(":templateId", templateId),
    params,
  );
export const postExternalCallback = (body: any, param): IApiPromise<any> =>
  api.post(Vendor.postExternalCallback, body, {
    params: { "g-recaptcha-response": param },
  } as any);

export const updateRequestedTemplateDetail = ({
  templateId,
  params,
}): IApiPromise<any> =>
  api.put(
    Vendor.requestedTemplateDetail.replace(":templateId", templateId),
    params,
  );
export const updateChecklistRequestedTemplateItem = ({
  templateId,
  params,
}): IApiPromise<any> =>
  api.put(
    Vendor.putChecklistRequestedTemplateItem.replace(":templateId", templateId),
    params,
  );
export const getVendorRequestedExternalInput = (): IApiPromise<any> =>
  api.get(Vendor.vendorRequestedExternalInput);

export const postRequestAssessmentAttachment = (
  checklistRequestedId: number,
  checklistItemId: number,
  formData: FormData,
): IApiPromise<any> =>
  api.post(
    Vendor.postRequestAssessmentAttachment.replace(
      /:checklistRequestedId|:checklistItemId/gi,
      (match) => {
        if (match === ":checklistRequestedId")
          return checklistRequestedId.toString();
        if (match === ":checklistItemId") return checklistItemId.toString();
        return "";
      },
    ),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

export const deleteRequestAssessmentAttachment = (
  checklistRequestedId: number,
  checklistItemId: number,
  attachmentIds: number[],
): IApiPromise<any> =>
  api.delete(
    Vendor.deleteRequestAssessmentAttachment.replace(
      /:checklistRequestedId|:checklistItemId/gi,
      (match) => {
        if (match === ":checklistRequestedId")
          return checklistRequestedId.toString();
        if (match === ":checklistItemId") return checklistItemId.toString();
        return "";
      },
    ),
    { data: { checklistItemAttachmentIds: attachmentIds } },
  );

export const deleteVendorRequestAssessmentAttachment = (
  checklistRequestedId: number,
  checklistItemId: number,
  attachmentIds: number[],
): IApiPromise<any> =>
  api.delete(
    Vendor.deleteVendorRequestAssessmentAttachment.replace(
      /:checklistRequestedId|:checklistItemId/gi,
      (match) => {
        if (match === ":checklistRequestedId")
          return checklistRequestedId.toString();
        if (match === ":checklistItemId") return checklistItemId.toString();
        return "";
      },
    ),
    { data: { checklistItemAttachmentIds: attachmentIds } },
  );

export const getChecklistRequestedComplianceTypeValues = (
  checklistContextIds = "",
): IApiPromise<any> => {
  let routes = Vendor.getChecklistRequestedComplianceTypeValues as string;

  if (checklistContextIds !== "")
    routes = routes + `?checklistContextIds[]=${checklistContextIds}`;

  return api.get(routes);
};
export const updateChecklistRequestedExternalItem = (
  checklistRequestedId: number,
  params: IParamsUpdateChecklistRequestedItems,
): IApiPromise<any> => {
  return api.put(
    Vendor.putChecklistRequestedExternalItem.replace(
      ":checklistRequestedId",
      checklistRequestedId.toString(),
    ),
    params,
  );
};

export const deleteChecklistRequestedAttachment = async (data: {
  checklistRequestedId: number;
  checklistItemId: number;
  checklistItemAttachmentIds: number[];
}): IApiPromise<any> => {
  return await api.delete(
    Vendor.deleteChecklistRequestedAttachment
      .replace(":checklistRequestedId", data.checklistRequestedId.toString())
      .replace(":checklistItemId", data.checklistItemId.toString()),
    { data: { checklistItemAttachmentIds: data.checklistItemAttachmentIds } },
  );
};

export const getRequestAssessmentAttachment = (
  attachmentId: number,
): IApiPromise<any> =>
  api.get(
    Vendor.getRequestAssessmentAttachment.replace(
      ":attachmentId",
      attachmentId.toString(),
    ),
  );

export const getRequestVendorAssessmentAttachment = (
  attachmentId: number,
): IApiPromise<any> =>
  api.get(
    Vendor.getRequestVendorAssessmentAttachment.replace(
      ":attachmentId",
      attachmentId.toString(),
    ),
  );

export const postRequestAssessmentEmailSubmit = (
  checklistRequestedId: number,
): IApiPromise<any> =>
  api.post(
    Vendor.postRequestAssessmentEmailSubmit.replace(
      ":checklistRequestedId",
      String(checklistRequestedId),
    ),
  );

export const getVendorProviders = async (): Promise<IVendorProvider[]> => {
  const res = await api.get(`${Org.getOrgSetup}?sections[]=providers`);
  const sections = get(res, "data.data.sections", []);

  if (sections.length === 0) return [];

  const providers = sections.find((s) => s.sectionId === "providers");

  if (!providers || !providers.questions || providers.questions.length === 0)
    return [];
  else return providers.questions[0].values || [];
};

export const postVendorProviders = async (
  payload: IVendorProvider,
): Promise<IVendorProvider> => {
  const body = {
    sectionId: "providers",
    questions: [
      {
        questionTypeId: "provider",
        values: [payload],
      },
    ],
  };

  try {
    const res = await api.post(Org.postProviderSetup, body);

    if (res.status !== successStatusCodes.OK) return {} as IVendorProvider;

    const sections = get(res, "data.data.sections", []);
    const providers = sections.find((s) => s.sectionId === "providers");

    if (!providers || !providers.questions || providers.questions.length === 0)
      return {} as IVendorProvider;

    const provider = providers.questions[0].values.find(
      (i) =>
        (i.name === payload.name && i.isArchived === false) ||
        i.id === payload.id,
    );

    return provider || ({} as IVendorProvider);
  } catch (error) {
    showErrorAPI(error, Org.postProviderSetup);
    return {} as IVendorProvider;
  }
};
export const updateChecklistRequestedEmailGrant = (
  params: {
    checklistRequestedId: number;
    emailGrantId: number;
  },
  body: {
    recipientEmail: string;
    offererOrgName: string;
    validFrom: string;
    validUntil: string;
    recipientFirstName?: string;
    recipientLastName?: string;
  },
): IApiPromise<IChecklistRequested[]> =>
  api.put(
    Vendor.putChecklistRequestedEmailGrant
      .replace(":checklistRequestedId", params.checklistRequestedId.toString())
      .replace(":emailGrantId", params.emailGrantId.toString()),
    body,
  );
export const createChecklistRequestedStatus = (
  params: { checklistRequestedId: number },
  body: { checklistRequestedStatusId: string },
): IApiPromise<Partial<IChecklistRequested>> =>
  api.post(
    Vendor.postChecklistRequestedStatus.replace(
      ":checklistRequestedId",
      params.checklistRequestedId.toString(),
    ),
    body,
  );

export const postChecklistResendVendor = async (
  checklistRequestedId: number,
  emailGrantId: number,
): IApiPromise<any> => {
  return await api.post(
    Vendor.postChecklistResendVendor
      .replace(":checklistRequestedId", checklistRequestedId.toString())
      .replace(":emailGrantId", emailGrantId.toString()),
  );
};

export const putRequestAssessmentEmail = (
  checklistRequestedId: number,
  notesResponse: string,
): IApiPromise<Partial<IChecklistRequested>> =>
  api.put(
    Vendor.putRequestAssessmentEmail.replace(
      ":checklistRequestedId",
      checklistRequestedId.toString(),
    ),
    {
      notesResponse,
    },
  );

export const getChecklistRequestedEmailGrants = (
  checklistRequestedId: number,
): IApiPromise<IDataEmailGrant> => {
  return api.get(
    Vendor.getChecklistRequestedEmailGrants.replace(
      ":checklistRequestedId",
      checklistRequestedId.toString(),
    ),
  );
};

export const deleteChecklistRequestedEmailGrant = (
  params: {
    checklistRequestedId: number;
    emailGrantId: number;
  },
  body: {
    isArchived: boolean;
  },
): IApiPromise<IChecklistRequested[]> =>
  api.put(
    Vendor.putChecklistRequestedEmailGrant
      .replace(":checklistRequestedId", params.checklistRequestedId.toString())
      .replace(":emailGrantId", params.emailGrantId.toString()),
    body,
  );

export const createChecklistRequestedRecipient = ({
  checklistRequestedId,
  recipientEmail,
  validUntil,
  recipientFirstName,
  recipientLastName,
}): IApiPromise<any> =>
  api.post(
    Vendor.createChecklistRequestedEmailGrant.replace(
      ":checklistRequestedId",
      checklistRequestedId,
    ),
    { recipientEmail, validUntil, recipientFirstName, recipientLastName },
  );

export const groupFilters = (filters) => {
  if (!filters || filters.length === 0) return [];
  return filters.reduce(
    (grouped, { targetId, value }) => {
      if (value.percent) {
        if (!grouped[targetId]) {
          grouped[targetId] = [value];
        } else {
          grouped[targetId].push(value);
        }
        return grouped;
      }
      if (!grouped[targetId]) grouped[targetId] = new Set();
      grouped[targetId].add(value.id);
      return grouped;
    },
    {} as Record<string, Set<any>>,
  );
};

export const filterChecklistByName = (
  listQuestion: any[],
  valueSearch: string,
) => {
  if (!listQuestion || listQuestion.length === 0) return [];
  if (!valueSearch) return listQuestion;

  const searchLowerCase = valueSearch.toLowerCase();

  return listQuestion.filter((item) => {
    const name = get(item, "checklistItemName", "").toLowerCase();

    if (item.checklistItemHasChildren && item.checklistItemHeadingLabel)
      return true;
    else return name.includes(searchLowerCase);
  });
};
